import cx from 'classnames';
import { Location } from 'history';
import { BZ2_NO_MORE_PRODUCTS } from 'mk/autogenerated/translations/Grid.befa20668a8943f81dd26a534fc2528c'
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import GridItem, { GridItemPlaceholder } from 'mk2/apps/bazaar/components/GridItem';
import GridStrollerItem from 'mk2/apps/bazaar/components/GridStrollerItem';
import { ITEMS_PER_PAGE } from 'mk2/apps/bazaar/constants';
import { Checked } from 'mk2/apps/bazaar/containers/HerBazaar/HerBazaar.actions';
import { ProductEntity } from 'mk2/apps/bazaar/schemas';
import { LoadingBar } from 'mk2/components/LoadingBar';
import { Panel } from 'mk2/components/Panel';
import { AdSlot } from 'mk2/containers/AdSlot/AdSlot';
import { ComplaintEntity } from 'mk2/containers/Complaint/Complaint.schemas';
import { StrollerEntity, StrollerPhoto, UserEntity } from 'mk2/schemas';
import React from 'react';
import styles from './Grid.mscss';

// indexes 0 - 15 are dedicated for strollers (if fetched), else show bz prods
const STROLLERS_FIRST_TILL_INDEX = 15;

// indexes below are special for rendering AdSlot before items with these indexes
const DESKTOP_ADSLOT_INDEXES = {
    8: 'Market Section - Mid 01 - Desktop',
};
const MOBILE_ADSLOT_INDEXES = {
    4: 'Market Section - Mid 01 - Mobile',
    12: 'Market Section - Mid 02 - Mobile',
    20: 'Market Section - Mid 03 - Mobile',
    28: 'Market Section - Mid 04 - Mobile',
    38: 'Market Section - Mid 05 - Mobile',
};

interface OwnProps {
    className?: string;
    products: ProductEntity[];

    strollers?: StrollerEntity[];
    strollerPhotosMap?: Record<number, StrollerPhoto>;

    requestUser: UserEntity;
    isModerator?: boolean;
    isAdmin?: boolean;
    showCreatedTime?: boolean;
    showStarredTime?: boolean;
    showStateBadges?: boolean;
    checked?: Checked;
    complaintsPerProduct?: {
        [productId: number]: ComplaintEntity[];
    };
    isMobile?: boolean;
    location: Location;
    isLoading?: boolean;

    onItemClick?(product: ProductEntity);
    onStrollerItemClick?(stroller: StrollerEntity, variantId: number, gridIndex: number);
}

type Props = OwnProps;

const Grid: React.FunctionComponent<Props> = ({
    className,
    products,

    strollers,
    strollerPhotosMap,

    requestUser,
    isModerator,
    isAdmin = false,
    isMobile = false,
    showCreatedTime = false,
    showStarredTime = false,
    showStateBadges = false,
    checked,
    complaintsPerProduct,
    location,
    isLoading = false,
    onItemClick,
    onStrollerItemClick,
}) => {
    const itemsCount = (products.length || 0) + (strollers?.length || 0);
    if (itemsCount === 0) {
        return <Panel className={styles.Grid__noResults}>{BZ2_NO_MORE_PRODUCTS}</Panel>;
    }

    let index = 0;
    let productIndex = 0;
    let strollerIndex = 0;
    let adIndex = 0;
    const items = [];

    while (index < itemsCount) {

        if (isMobile && MOBILE_ADSLOT_INDEXES[index] && itemsCount >= index + 2) {
            items.push(<React.Fragment key={`ad-${adIndex}`}>
                <AdSlot zone={MOBILE_ADSLOT_INDEXES[index]} location={location}/>
            </React.Fragment>);
            adIndex += 1;
        }
        else if (!isMobile && DESKTOP_ADSLOT_INDEXES[index] && itemsCount >= index + 4) {
            items.push(<React.Fragment key={`ad-${adIndex}`}>
                <AdSlot zone={DESKTOP_ADSLOT_INDEXES[index]} location={location} marginTopWithAd={20}/>
            </React.Fragment>);
            adIndex += 1;
        }

        if (strollers?.length && index <= STROLLERS_FIRST_TILL_INDEX && strollers.length > strollerIndex) {
            const stroller = strollers[strollerIndex];
            items.push(<React.Fragment key={`stroller-${strollerIndex}`}>
                <GridStrollerItem
                    stroller={stroller}
                    className={cx(styles.Grid__item, styles[`Grid__item--col${index % 4}`])}
                    strollerPhotosMap={strollerPhotosMap}
                    gridIndex={index}
                    onClick={onStrollerItemClick}
                />
            </React.Fragment>);
            strollerIndex += 1;
        }

        else if (products?.length && products.length > productIndex) {
            const product = products[productIndex];
            items.push(<React.Fragment key={`product-${productIndex}`}>
                <GridItem
                    product={product}
                    className={cx(styles.Grid__item, styles[`Grid__item--col${index % 4}`])}
                    isModerator={isModerator}
                    isSeller={isAuthenticatedAs(requestUser, product.seller.user)}
                    showCreatedTime={showCreatedTime}
                    showStarredTime={showStarredTime}
                    showStateBadges={showStateBadges}
                    checked={checked[product.id]}
                    complaintsCount={
                        complaintsPerProduct[product.id] ? complaintsPerProduct[product.id].length : 0
                    }
                    onClick={onItemClick}
                />
            </React.Fragment>);
            productIndex += 1;
        }
        index += 1;
    }
    return (
        <div className={cx(styles.Grid, className)}>
            <LoadingBar loading={isLoading} />
            <div className={cx(styles.Grid__products, isLoading && styles['Grid__products--loading'])}>
                {items}
            </div>
        </div>
    );
};

Grid.displayName = 'Grid';

export default React.memo<Props>(Grid);

interface PlaceholderOwnProps {
    className?: string;
    productsCount?: number;
}

let GridPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className, productsCount = ITEMS_PER_PAGE }) => (
    <div className={cx(styles.Grid, className)}>
        {Array.from({ length: productsCount }, (v, index) => (
            <GridItemPlaceholder className={cx(styles.Grid__item, styles[`Grid__item--col${index % 4}`])} key={index} />
        ))}
    </div>
);

GridPlaceholder.displayName = 'GridPlaceholder';

GridPlaceholder = React.memo<PlaceholderOwnProps>(GridPlaceholder);

export { GridPlaceholder };
